import React, { useEffect, useState } from 'react';
import { useAudio } from './hooks/useAudio';

interface SpotlightAnimationProps{
  questionId: string;
  feedback?: 'none' | 'correct' | 'wrong';
}

/**
 * KBC-style Spotlight Animation Component
 * Creates theatrical lighting effect with sweeping beams, hotspot, and gloss
 * Plays kbc-question.mp3 (5 seconds) synchronized with animation
 */
const SpotlightAnimation: React.FC<SpotlightAnimationProps> = ({ questionId, feedback }) => {
  const [showFeedback, setShowFeedback] = useState(false);
  const { audioMuted } = useAudio();

  // Play kbc-question audio when question changes
  useEffect(() => {
    if (audioMuted) return;

    const audio = new Audio('/audio/kbc-question.mp3');
    // FAILSAFE: Set volume based on audio state
    audio.volume = audioMuted ? 0 : 0.5;
    audio.play().catch(() => {
      console.debug('kbc-question audio not available');
    });

    return () => {
      audio.pause();
      audio.currentTime = 0;
    };
  }, [questionId, audioMuted]);

  useEffect(() => {
    if (feedback !== 'none') {
      setShowFeedback(true);
      const timer = setTimeout(() => setShowFeedback(false), 300);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  // Check for reduced motion preference
  const prefersReducedMotion = typeof window !== 'undefined' && 
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReducedMotion) {
    // Fallback: simple fade-in for accessibility
    return (
      <div 
        key={questionId}
        className="absolute inset-0 pointer-events-none animate-fade-in"
      />
    );
  }

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {/* Dual sweeping conical beams */}
      <div className="spotlight-beam-container">
        <div className="spotlight-beam left" />
        <div className="spotlight-beam right" />
        <div className="spotlight-ambient-glow" />
      </div>
      {/* Initial Dark Stage - fades in quickly */}
      <div 
        key={`dark-stage-${questionId}`}
        className="absolute inset-0 bg-black/70 animate-fade-in"
        style={{
          animationDuration: '0.3s',
        }}
      />

      {/* Spotlight Reveal - builds up then focuses (5 seconds to match audio) */}
      <div 
        key={`spotlight-reveal-${questionId}`}
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse 100% 80% at center 40%, rgba(255, 215, 0, 0.08) 0%, rgba(0, 115, 183, 0.04) 40%, transparent 80%)',
          mixBlendMode: 'screen',
          animation: 'spotlightReveal 5s cubic-bezier(0.4, 0, 0.2, 1) forwards',
        }}
      />

      {/* Correct Answer Pulse */}
      {showFeedback && feedback === 'correct' && (
        <div
          className="absolute inset-0 animate-correct-pulse"
          style={{
            background: 'radial-gradient(circle at center, rgba(255, 215, 0, 0.3) 0%, transparent 60%)',
            mixBlendMode: 'screen',
          }}
        />
      )}

      {/* Wrong Answer Flash */}
      {showFeedback && feedback === 'wrong' && (
        <div
          className="absolute inset-0 animate-wrong-flash"
          style={{
            background: 'radial-gradient(circle at center, rgba(220, 20, 60, 0.4) 0%, transparent 60%)',
            mixBlendMode: 'screen',
          }}
        />
      )}
    </div>
  );
};

export default SpotlightAnimation;
